import { ChevronsDown, ExternalLink, FileCode2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type FileLevelMetric = {
  label: string;
  value: number | string;
};

type FileLevelBreakdownRow = {
  id: string;
  path: string;
  metrics: FileLevelMetric[];
  onViewCode?: () => void;
  onViewEntities?: () => void;
};

type FileLevelBreakdownProps = {
  rows: FileLevelBreakdownRow[];
  className?: string;
};

/**
 * Always-visible file metrics — no horizontal drag required.
 * Each file is a bordered row: path + actions on top, stats grid below.
 */
function FileLevelBreakdown({ rows, className }: FileLevelBreakdownProps) {
  if (!rows.length) return null;

  return (
    <div
      className={cn(
        'box-border overflow-hidden rounded-[12px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]',
        className,
      )}>
      <div className="box-border flex items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-4 py-2.5">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]">
          File path
        </span>
        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]">
          {rows.length.toLocaleString()} file{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      <ul className="m-0 max-h-[min(32rem,60vh)] list-none overflow-y-auto p-0">
        {rows.map(row => (
          <li
            key={row.id}
            className="box-border border-0 border-b border-solid border-[var(--inv-border-soft)] last:border-b-0">
            <div className="box-border flex items-start gap-3 px-4 py-3 transition-colors hover:bg-[var(--inv-surface-muted)]">
              <FileCode2
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--inv-text-muted)]"
                aria-hidden="true"
              />

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <p
                    className="m-0 min-w-0 break-all font-mono text-[0.8125rem] leading-snug text-[var(--inv-text)]"
                    title={row.path}>
                    {row.path}
                  </p>
                  <div className="flex shrink-0 items-center gap-0.5">
                    {row.onViewCode ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 border-0 p-0 text-[var(--inv-accent)] shadow-none hover:bg-[var(--inv-accent-soft)] hover:text-[var(--inv-accent-hover)] hover:cursor-pointer"
                        onClick={row.onViewCode}
                        aria-label={`View code for ${row.path}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    ) : null}
                    {row.onViewEntities ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 border-0 p-0 text-[var(--inv-accent)] shadow-none hover:bg-[var(--inv-accent-soft)] hover:text-[var(--inv-accent-hover)] hover:cursor-pointer"
                        onClick={row.onViewEntities}
                        aria-label={`Entity details for ${row.path}`}>
                        <ChevronsDown className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4 lg:grid-cols-8">
                  {row.metrics.map(metric => (
                    <div key={metric.label} className="min-w-0">
                      <div className="text-[0.625rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                        {metric.label}
                      </div>
                      <div className="tabular-nums text-[0.8125rem] font-medium text-[var(--inv-text)]">
                        {typeof metric.value === 'number'
                          ? metric.value.toLocaleString()
                          : metric.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export { FileLevelBreakdown };
export type { FileLevelBreakdownRow, FileLevelBreakdownProps };
